import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  History,
  CheckCircle,
  XCircle,
  Clock,
  MapPin,
  Phone,
  User,
  Calendar,
  TrendingUp
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RequestHistory = ({ user }) => {
  const navigate = useNavigate();
  const [history, setHistory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/veteran/request-history`);
      setHistory(response.data);
    } catch (error) {
      console.error("Failed to load history:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-600"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case "cancelled":
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      case "pending":
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case "accepted":
        return <Badge className="bg-blue-600"><User className="w-3 h-3 mr-1" />In Progress</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "R";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="va-blue-bar h-2"></div>
      
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
            <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Support History
            </span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Summary Stats */}
        {history?.summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <Card className="card-valor">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-primary">{history.summary.total_requests}</p>
                <p className="text-sm text-muted-foreground">Total Requests</p>
              </CardContent>
            </Card>
            <Card className="card-valor">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{history.summary.completed}</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </CardContent>
            </Card>
            <Card className="card-valor">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-red-500">{history.summary.cancelled}</p>
                <p className="text-sm text-muted-foreground">Cancelled</p>
              </CardContent>
            </Card>
            <Card className="card-valor">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-secondary">{history.summary.completion_rate}%</p>
                <p className="text-sm text-muted-foreground">Completion Rate</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Request List */}
        <Card className="card-valor">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <History className="w-5 h-5 text-primary" />
              Your Support Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!history?.requests || history.requests.length === 0 ? (
              <div className="text-center py-12">
                <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No support requests yet</p>
                <Button 
                  className="mt-4"
                  onClick={() => navigate("/dashboard")}
                >
                  Request Support
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {history.requests.map((request) => (
                  <div 
                    key={request.request_id}
                    className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          request.request_type === "in_person" ? "bg-primary/10" : "bg-secondary/10"
                        }`}>
                          {request.request_type === "in_person" ? (
                            <MapPin className="w-5 h-5 text-primary" />
                          ) : (
                            <Phone className="w-5 h-5 text-secondary" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-foreground">
                              {request.request_type === "in_person" ? "In-Person Support" : "Phone/Video Call"}
                            </span>
                            {getStatusBadge(request.status)}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="w-3 h-3" />
                            {formatDate(request.created_at)}
                          </div>
                          {request.description && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {request.description}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Responder Info */}
                      {request.responder_info && (
                        <div className="flex items-center gap-3 bg-muted/50 rounded-lg p-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={request.responder_picture} />
                            <AvatarFallback className="bg-primary text-white">
                              {getInitials(request.responder_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">{request.responder_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {request.responder_info.applicant_type === "civilian" 
                                ? request.responder_info.professional_background 
                                : request.responder_info.military_branch}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Timeline */}
                    {request.status === "completed" && request.completed_at && (
                      <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-600" />
                          Completed on {formatDate(request.completed_at)}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default RequestHistory;
